import { useEffect, useState } from 'react';
import { useApp, useFetch } from '../store/app';
import { Fade } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, Avatar } from '../components/primitives';
import { av } from '../lib/avatar';
import { api } from '../lib/api';
import type { DataPoint, Team } from '../../shared/types';

interface Match { id: number; name: string; city: string; state: string; dist: string; miles: number | null; kind: string; best: boolean; tags: string[] }
interface LocRow { loc: string; client: string; label: string; location_id: number | null; matched: boolean; matches: Match[] }

export function Assignments() {
  const { refresh } = useApp();
  const { data: projects } = useFetch<{ id: number; name: string }[]>('/projects');
  const { data: teams } = useFetch<Team[]>('/teams');
  const { data: dataPoints } = useFetch<DataPoint[]>('/datapoints');

  const [projectId, setProjectId] = useState<number | ''>('');
  const [createProject, setCreateProject] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [teamId, setTeamId] = useState<number | ''>('');
  const [tags, setTags] = useState<string[]>([]);
  const [match, setMatch] = useState<'all' | 'any'>('any');
  const [locations, setLocations] = useState('');
  const [rows, setRows] = useState<LocRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [assigned, setAssigned] = useState<Record<string, boolean>>({});
  const [note, setNote] = useState('');

  useEffect(() => {
    if (projects?.length && projectId === '') setProjectId(projects[0].id);
  }, [projects]);

  const tagOptions = Array.from(new Set((dataPoints || []).filter((d) => d.group === 'Skills').map((d) => d.value)));
  const toggleTag = (t: string) => setTags((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  const findNearest = async () => {
    setBusy(true);
    setNote('');
    try {
      const res = await api.post<{ rows: LocRow[] }>('/assignments/rank', {
        locations: locations.split('\n'),
        team_id: teamId || null,
        tags,
        match,
      });
      setRows(res.rows);
      setAssigned({});
      if (!res.rows.length) setNote('Paste at least one location.');
    } finally {
      setBusy(false);
    }
  };

  const assign = async (row: LocRow, m: Match) => {
    if (createProject && !row.location_id) {
      setNote('That location has no matched client site, so a project can’t be created from it. Uncheck “Create a project” and pick an existing project, or paste a location that matches a client facility.');
      return;
    }
    if (!createProject && !projectId) {
      setNote('Pick a project first (or enable “Create a project from the location”).');
      return;
    }
    const res = await api.post<{ project_name?: string; created_project?: boolean }>('/assignments', {
      person_id: m.id,
      project_id: createProject ? undefined : projectId,
      create_project: createProject,
      location_id: row.location_id,
      start_date: startDate,
    });
    setAssigned((s) => ({ ...s, [`${row.loc}-${m.id}`]: true }));
    const proj = res?.project_name ? `“${res.project_name}”` : (row.client || row.loc);
    const projNote = res?.created_project ? `Created project ${proj} and assigned ${m.name}` : `Assigned ${m.name} → ${proj}`;
    setNote(`${projNote}. ${m.name} emailed with you on CC + Reply-To.`);
    refresh();
  };

  return (
    <Fade>
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 16, alignItems: 'start' }}>
        <Card style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 3 }}>Assign to locations</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Paste locations (one per row). CCSilo matches each to a client site and ranks the nearest staff.</div>
          </div>
          <div style={{ padding: '14px 16px' }}>
            <label className="cc-clickable" style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 14 }}>
              <input type="checkbox" checked={createProject} onChange={(e) => setCreateProject(e.target.checked)} style={{ marginTop: 2, accentColor: 'var(--accent)', width: 15, height: 15 }} />
              <span>
                <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>Create a project from the location</span>
                <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Names the project after the facility, sets its client, and adds you + the staff member to the team.</span>
              </span>
            </label>

            {!createProject && (
              <>
                <Label>Project</Label>
                <Select value={String(projectId)} onChange={(v) => setProjectId(Number(v))} options={(projects || []).map((p) => ({ label: p.name, value: String(p.id) }))} />
              </>
            )}

            <Label>Start date</Label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />

            <Label>Team (optional)</Label>
            <Select value={String(teamId)} onChange={(v) => setTeamId(v ? Number(v) : '')} options={[{ label: 'Any staff', value: '' }, ...(teams || []).map((t) => ({ label: `${t.name} (${t.member_count ?? t.member_ids.length})`, value: String(t.id) }))]} />

            <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
              <Label>Required tags</Label>
              <div style={{ flex: 1 }} />
              {tags.length > 1 && (
                <div style={{ display: 'inline-flex', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7, padding: 2, marginBottom: 7 }}>
                  {(['any', 'all'] as const).map((mode) => (
                    <button key={mode} type="button" onClick={() => setMatch(mode)} style={{ appearance: 'none', cursor: 'pointer', border: 0, borderRadius: 5, padding: '3px 9px', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, background: match === mode ? 'var(--surface)' : 'transparent', color: match === mode ? 'var(--text)' : 'var(--text-3)' }}>
                      {mode === 'any' ? 'Match any' : 'Match all'}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {tagOptions.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>No skill tags defined.</span>}
              {tagOptions.map((t) => {
                const on = tags.includes(t);
                return (
                  <button key={t} type="button" onClick={() => toggleTag(t)} style={{ appearance: 'none', cursor: 'pointer', border: `1px solid ${on ? 'var(--accent)' : 'var(--border-2)'}`, background: on ? 'var(--accent-bg)' : 'var(--surface)', color: on ? 'var(--accent)' : 'var(--text-2)', borderRadius: 20, padding: '4px 11px', fontFamily: 'inherit', fontSize: 12, fontWeight: 600 }}>
                    {t}
                  </button>
                );
              })}
            </div>

            <Label>Locations</Label>
            <textarea value={locations} onChange={(e) => setLocations(e.target.value)} placeholder={'El Paso, TX 79901\nTucson, AZ 85701'} className="cc-mono" style={{ ...inputStyle, background: 'var(--surface-2)', lineHeight: 1.9, minHeight: 92, resize: 'vertical' }} />
            <Button icon="distance" full onClick={findNearest} disabled={busy} style={{ marginTop: 12, padding: '10px 14px' }}>{busy ? 'Ranking…' : 'Find nearest staff'}</Button>
            {note && <div style={{ marginTop: 10, fontSize: 12, color: 'var(--success)', textAlign: 'center', lineHeight: 1.4 }}>{note}</div>}
          </div>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rows.length === 0 && (
            <Card style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
              Paste locations and click <strong>Find nearest staff</strong> to see ranked candidates.
            </Card>
          )}
          {rows.map((row) => (
            <Card key={row.loc} style={{ overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: '1px solid var(--border)' }}>
                <Icon name="location_on" size={18} color="var(--text-3)" />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{row.loc}</div>
                  <div style={{ fontSize: 11.5, color: row.matched ? 'var(--text-3)' : 'var(--warning)' }}>
                    {row.matched ? (row.client ? `Matched: ${row.client} — ${row.label}` : 'Matched by ZIP') : 'No matching client location — ranking unavailable'}
                  </div>
                </div>
              </div>
              {row.matches.map((m, i) => {
                const a = av(m.name, m.kind);
                const done = assigned[`${row.loc}-${m.id}`];
                return (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderTop: i === 0 ? 0 : '1px solid var(--border)' }}>
                    <Avatar initials={a.in} bg={a.bg} fg={a.fg} size={34} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ fontWeight: 600, fontSize: 13.5 }}>{m.name}</span>
                        {m.best && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.04em', color: 'var(--success)', background: 'var(--success-bg)', padding: '2px 6px', borderRadius: 4 }}>CLOSEST</span>}
                        {m.tags.map((t) => <span key={t} style={{ fontSize: 10, fontWeight: 600, color: 'var(--accent)', background: 'var(--accent-bg)', padding: '1px 6px', borderRadius: 4 }}>{t}</span>)}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{[m.city, m.state].filter(Boolean).join(', ')} · {m.dist}</div>
                    </div>
                    <Button variant={done ? 'ghost' : 'secondary'} icon={done ? 'check' : undefined} onClick={() => assign(row, m)} disabled={done} style={{ padding: '6px 12px' }}>{done ? 'Assigned' : 'Assign'}</Button>
                  </div>
                );
              })}
              {row.matches.length === 0 && <div style={{ padding: '14px 16px', fontSize: 12.5, color: 'var(--text-3)' }}>No staff match the selected team/tags.</div>}
            </Card>
          ))}
        </div>
      </div>
    </Fade>
  );
}

const inputStyle = { border: '1px solid var(--border-2)', borderRadius: 8, background: 'var(--surface)', padding: '9px 11px', fontSize: 13, color: 'var(--text)', width: '100%', outline: 'none', fontFamily: 'inherit', marginBottom: 14 } as const;

function Label({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 7 }}>{children}</div>;
}

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { label: string; value: string }[] }) {
  return (
    <div style={{ position: 'relative', marginBottom: 14 }}>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, marginBottom: 0, appearance: 'none', cursor: 'pointer', paddingRight: 32 }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <Icon name="expand_more" size={18} color="var(--text-3)" style={{ position: 'absolute', right: 10, top: 10, pointerEvents: 'none' }} />
    </div>
  );
}
